import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { FC, useEffect, useMemo, useState } from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import cn from './index.module.css'
import { fetchAllGroups } from '../../utils/api'
import { getClassesIndex } from '@pages/Admin/Reports/Payments/utils/api'

interface ISeniorSeminaristResponsibilitySelect {
  selectedClasses: number[]
  selectedGroups: number[]
  onChange: (classes: number[], groups: number[]) => void
  isInvalid?: boolean
}

const SeniorSeminaristResponsibilitySelect: FC<
  ISeniorSeminaristResponsibilitySelect
> = ({ selectedClasses, selectedGroups, onChange, isInvalid }) => {
  const { checkSettings } = useGetSettings()
  const showSelect = checkSettings('isSeniorSeminarianManageCoursesClasses')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [options, setOptions] = useState<ISelect[]>([])

  const fetchSettings = async () => {
    setIsLoading(true)
    try {
      const [groups, classes] = await Promise.all([
        fetchAllGroups(),
        getClassesIndex(),
      ])

      const classesSelect = classes.map((item) => ({
        label:
          'Класс: ' +
          (item.title ?? item.id) +
          (item?.supervisor ? ` (Куратор: ${item?.supervisor?.name})` : ''),
        value: 'class_' + item.id!.toString(),
        disabled:
          !!item?.supervisor &&
          !selectedClasses?.some((id) => '' + id === '' + item.id),
      }))

      const groupSelect = groups.map((item) => ({
        label:
          'Группа: ' +
          (item.name ?? item.id) +
          (item?.supervisor ? ` (Куратор: ${item?.supervisor?.name})` : ''),
        value: 'group_' + item.id!.toString(),
        disabled:
          !!item?.supervisor &&
          !selectedGroups?.some((id) => '' + id === '' + item.id),
      }))

      setOptions([...classesSelect, ...groupSelect])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const selectedValue = useMemo(() => {
    return [
      ...selectedClasses?.map((id) => `class_${id}`),
      ...selectedGroups?.map((id) => `group_${id}`),
    ]
  }, [selectedClasses, selectedGroups])

  const onChangeSelected = (value: string[]) => {
    const classes: number[] = []
    const groups: number[] = []

    value.forEach((v) => {
      if (v.startsWith('class_')) {
        const id = parseInt(v.replace('class_', ''))
        if (!isNaN(id)) classes.push(id)
      } else if (v.startsWith('group_')) {
        const id = parseInt(v.replace('group_', ''))
        if (!isNaN(id)) groups.push(id)
      }
    })

    onChange(classes, groups)
  }

  if (!showSelect) return <></>

  return (
    <div className={cn.row}>
      <SelectField
        className={cn.select}
        label="Классы/группы для курирования"
        placeholder="классы/группы"
        mode="multiple"
        value={selectedValue}
        onChange={(value) => {
          if (Array.isArray(value)) onChangeSelected(value)
        }}
        options={options}
        isInvalid={isInvalid}
        loading={isLoading}
      />
      <TooltipWrapper
        title={
          'В этом поле необходимо выбрать все классы/группы, которые будет курировать данный старший семинарист'
        }
      >
        <InfoCircleOutlined
          className={cn.infoIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </TooltipWrapper>
    </div>
  )
}

export default SeniorSeminaristResponsibilitySelect
